<template>
  <v-container fluid>
    <v-row class="d-flex flex-row">
      <v-col cols="12">
        <FormCulturalOrganization
          :data="culturalOrganization"
          :cities="cities"
          :loading="isFormLoading"
          :loadingData="isMutationLoading"
          :shared-access="sharedAccess"
          :ticket-reservations="ticketReservations"
          :isNew="!id"
          :language="language"
          @submit="formCulturalOrganisationSubmit"
          @delete-cultural-organisation="deleteCulturalOrganization"
          @language-update="language = $event"
          @change:descriptionForMobile="changeDescForMobHandler($event)"
        />
      </v-col>
    </v-row>
    <ConfirmationDialog ref="confirmCulturaOrganisationRemoving" confirmText="Удалить" />
  </v-container>
</template>

<script>
import FormCulturalOrganization from './form'
import ConfirmationDialog from '@/components/confirmation-dialog'
import culturalOrganization from '@/services/gql/types/CulturalOrganizations/queries/culturalOrganization.gql'
import citiesQuery from '@/services/gql/types/City/queries/cities.gql'
import createCulturalOrganization from '@/services/gql/types/CulturalOrganizations/mutations/createCulturalOrganization.gql'
import deleteCulturalOrganization from '@/services/gql/types/CulturalOrganizations/mutations/deleteCulturalOrganization.gql'
import updateCulturalOrganization from '@/services/gql/types/CulturalOrganizations/mutations/updateCulturalOrganization.gql'
import { contentLocales, contentLocalesValues } from '@/configs/locales'
import { getListWithNewMainLanguage, setTranslation } from '@/utils/locales'
import sharedAccessQuery from '@/services/gql/types/SharedAccess/queries/sharedAccess.gql'
import ticketReservationsOfRelations from '@/services/gql/types/TicketReservations/queries/ticketReservationsOfRelations.gql'

export default {
  name: 'PageCulturalOrganization',
  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },
  components: {
    FormCulturalOrganization,
    ConfirmationDialog
  },
  metaInfo () {
    return {
      title: this.id
        ? 'Редактировать организацию культуры'
        : 'Создать организацию культуры'
    }
  },
  data () {
    return {
      language: contentLocales[0].value,
      culturalOrganization: {},
      cities: null,
      sharedAccess: null,
      ticketReservations: null
    }
  },
  computed: {
    isFormLoading () {
      return (
        this.$apollo.queries.culturalOrganization.loading ||
        this.$apollo.queries.cities.loading
      )
    },
    isMutationLoading () {
      return (
        this.$async.createCulturalOrganization.$pending ||
        this.$async.updateCulturalOrganization.$pending
      )
    }
  },
  methods: {
    changeDescForMobHandler ({ fields, $event }) {
      fields.descriptionForMobile = $event
    },
    async formCulturalOrganisationSubmit (сulturalOrganisationPayload) {
      if (!this.id) {
        await this.createCulturalOrganization(сulturalOrganisationPayload)
      } else {
        await this.updateCulturalOrganization(сulturalOrganisationPayload)
      }
    },
    async deleteCulturalOrganization () {
      try {
        await this.$refs.confirmCulturaOrganisationRemoving.open()
        await this.$async.deleteCulturalOrganization.$perform({ id: Number(this.id) })
        this.$successMsg('Организация культуры удалена')
      } catch (e) {
        this.$handleApiError(
          e,
          `Ошибка при удалении организации культуры с ID ${this.id}`
        )
      } finally {
        await this.$router.push({ name: 'page-cultural-organizations' })
      }
    },
    async updateCulturalOrganization (data) {
      try {
        setTranslation(data, this.language)
        await this.$async.updateCulturalOrganization.$perform(
          {
            id: Number(this.id),
            data
          },
          { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) }
        )
        this.$successMsg('Организация культуры изменена')
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при редактировании организации культуры')
      }
    },
    async createCulturalOrganization (data) {
      try {
        delete data.translations
        await this.$async.createCulturalOrganization.$perform({
          data
        })
        this.$successMsg('Организация культуры создана')
        await this.$router.push({ name: 'page-cultural-organizations' })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при создании организации культуры')
      }
    }
  },
  apollo: {
    culturalOrganization: {
      query: culturalOrganization,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            id: Number(this.id)
          }
        }
      },
      context () {
        return {
          headers: {
            Language: getListWithNewMainLanguage(this.language, contentLocalesValues())
          }
        }
      },
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-cultural-organizations' })
        } else {
          this.culturalOrganization = {
            ...res?.data?.culturalOrganization,
            cityId: res?.data?.guide?.city?.id || null,
            imageId: res?.data?.guide?.image?.id || null
          }
        }
      }
    },
    cities: {
      query: citiesQuery,
      fetchPolicy: 'network-only',
      pollInterval: 30000,
      variables: {
        where: {
          status_not: 'DRAFT'
        }
      },
      context () {
        return { headers: { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) } }
      },
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          // this.$router.push({ name: 'page-cultural-organizations' })
        } else {
          this.cities =
            res?.data?.cities?.list.map(city => ({
              id: city.id,
              title: city.title
            })) || null
        }
      }
    },
    sharedAccess: {
      query: sharedAccessQuery,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            profile: {
              id: Number(this.id)
            }
          }
        }
      },
      context () { return { headers: { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) } } },
      result (res) {
        this.sharedAccess = res?.data?.sharedAccess?.list
      }
    },
    ticketReservations: {
      query: ticketReservationsOfRelations,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            profileType: 'GUIDE',
            profileId: Number(this.id)
          }
        }
      },
      context () { return { headers: { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) } } },
      result (res) {
        const { list } = res?.data?.ticketReservations
        const tickets = list
          ?.map(ticket => ({ count: 1, name: ticket.displayName }))
          ?.reduce((acc, ticket) => {
            acc[ticket.name] = (acc[ticket.name] || 0) + ticket.count
            return acc
          }, {}) || null
        this.ticketReservations = Object.keys(tickets)?.map(ticket => ({
          title: ticket + ': ' + tickets[ticket]
        }))
      }
    }
  },
  async: {
    createCulturalOrganization (variables) {
      return this.$apollo.mutate({
        mutation: createCulturalOrganization,
        variables
      })
    },
    deleteCulturalOrganization (variables) {
      return this.$apollo.mutate({
        mutation: deleteCulturalOrganization,
        variables
      })
    },
    updateCulturalOrganization (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateCulturalOrganization,
        variables,
        context: {
          headers
        }
      })
    }
  }
}
</script>
